/**
 * Buyer-facing card detail modal (TNK-115 phase B2). Opened from the
 * `/shop/inventory` grid for a fast in-place look at a card without
 * losing the operator's scroll position. The full `/shop/card/$id`
 * page stays as the share-link target; this modal is purely a UX
 * accelerator for browsing.
 *
 * Architecture: shares all visual tokens with `shop.card.$id.tsx` via
 * `./cardVisuals` so the rarity glow / game accent / type chip palette
 * stays in lockstep across both surfaces. Layout / Tailwind classes are
 * intentionally repeated rather than abstracted — the modal's chrome
 * (backdrop, prev/next, keyboard hints, slide-between-cards) is enough
 * different from the page that a shared component would be a net loss.
 *
 * Keyboard:
 *   ← / →  navigate to prev / next card in the visible inventory slice
 *   Esc    close
 *   Backdrop click also closes (the panel itself stops propagation).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Gavel,
  RefreshCw,
  ShoppingBag,
  X,
  ZoomIn,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "@tanstack/react-router";

import { imageUrl } from "@/features/imageUpload";
import { storefrontPrice, type StorefrontCard } from "@/features/storefront";
import {
  asCondition,
  asGame,
  asRarity,
  conditionConfig,
  gameAccent,
  pokeTypeColor,
  rarityConfig,
  type ConditionCode,
  type Rarity,
} from "./cardVisuals";
import { CardLightbox } from "./CardLightbox";
import { ManaCost } from "./ManaCost";

interface CardDetailModalProps {
  /** Current card being viewed. `null` closes the modal. */
  card: StorefrontCard | null;
  /** Ordered slice the modal navigates within (visible/filtered inventory). */
  cards: StorefrontCard[];
  /** Ids already in the buyer's cart — drives the Reserve / Reserved state. */
  reservedIds: string[];
  onReserve: (id: string) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
  /** When the buyer hits ← / →, swap to that card without closing. */
  onNavigate: (id: string) => void;
}

export function CardDetailModal({
  card,
  cards,
  reservedIds,
  onReserve,
  onRemove,
  onClose,
  onNavigate,
}: CardDetailModalProps) {
  const [flipped, setFlipped] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Reset transient state on card swap so the next card opens fresh.
  useEffect(() => {
    setFlipped(false);
    setRemoveConfirm(false);
    setLightboxOpen(false);
  }, [card?.id]);

  const idx = card ? cards.findIndex((c) => c.id === card.id) : -1;
  const hasPrev = idx > 0;
  const hasNext = idx >= 0 && idx < cards.length - 1;

  const navigate = useCallback(
    (dir: 1 | -1) => {
      if (idx < 0) return;
      const next = cards[idx + dir];
      if (!next) return;
      setDirection(dir);
      onNavigate(next.id);
    },
    [cards, idx, onNavigate],
  );

  useEffect(() => {
    if (!card) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) navigate(-1);
      if (e.key === "ArrowRight" && hasNext) navigate(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [card, hasPrev, hasNext, navigate, onClose]);

  if (!card) return null;

  const rarity = asRarity(card.rarity);
  const rarityCfg = rarityConfig[rarity];
  const game = asGame(card.game);
  const gameCfg = gameAccent[game];
  const condition = asCondition(card.condition);
  const isReserved = reservedIds.includes(card.id);
  // TNK-119: when the card has an active eBay auction listing, the
  // buyer goes to eBay to bid — local Reserve doesn't apply.
  const isAuction = card.listingType === "Auction" && !!card.ebayListingUrl;
  const price = storefrontPrice(card);

  const metaRows: { label: string; value: React.ReactNode }[] = [];
  metaRows.push({ label: "Set", value: card.setName });
  metaRows.push({
    label: "Number",
    value: <span className="shop-mono text-[#4d9ef7]">{card.cardNumber}</span>,
  });
  if (card.variant) metaRows.push({ label: "Variant", value: card.variant });
  if (card.finish) {
    metaRows.push({
      label: "Finish",
      value: (
        <span
          className="rounded-sm border px-1.5 py-0.5 text-[10px]"
          style={{
            background: "rgba(168,85,247,0.15)",
            borderColor: "rgba(168,85,247,0.3)",
            color: "#d8b4fe",
          }}
        >
          {card.finish}
        </span>
      ),
    });
  }
  if (card.year) metaRows.push({ label: "Year", value: card.year });
  if (card.language)
    metaRows.push({
      label: "Language",
      value: <span className="text-[#fb923c]">{card.language}</span>,
    });
  if (card.tcgSeries) metaRows.push({ label: "Series", value: card.tcgSeries });
  if (card.tcgArtist)
    metaRows.push({ label: "Illustrator", value: card.tcgArtist });
  if (card.tcgPokedexNumbers && card.tcgPokedexNumbers.length > 0) {
    metaRows.push({
      label: "Pokédex",
      value: card.tcgPokedexNumbers
        .map((n) => `#${String(n).padStart(3, "0")}`)
        .join(", "),
    });
  }
  // TNK-121: Magic colors row, same shape as the detail page.
  if (card.mtgColors !== null && card.game === "Magic") {
    metaRows.push({
      label: card.mtgColors.length === 1 ? "Color" : "Colors",
      value:
        card.mtgColors.length === 0 ? (
          <span className="text-muted-foreground">Colorless</span>
        ) : (
          <ManaCost
            cost={card.mtgColors.map((c) => `{${c}}`).join("")}
            size={14}
          />
        ),
    });
  }

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-40"
        style={{
          background: "rgba(4,6,14,0.88)",
          backdropFilter: "blur(10px)",
        }}
        onClick={onClose}
      />

      <motion.div
        key="modal"
        initial={{ opacity: 0, scale: 0.9, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 16 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          className="relative w-full max-w-[680px] max-h-[calc(100vh-2rem)] overflow-y-auto overflow-x-hidden overscroll-contain rounded-2xl border bg-card pointer-events-auto"
          style={{
            background: "linear-gradient(160deg,#0b1022 0%,#08101e 100%)",
            borderColor: `${rarityCfg.color}22`,
            boxShadow: `0 32px 80px rgba(0,0,0,0.85), 0 0 60px ${rarityCfg.glow}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top accent strip */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background: `linear-gradient(90deg, transparent, ${rarityCfg.color}88, transparent)`,
            }}
          />

          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
          >
            <X size={14} />
          </button>

          <div className="px-5 pb-5 pt-5">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={card.id}
                custom={direction}
                initial={{ opacity: 0, x: direction * 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -40 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="flex flex-col gap-6 sm:flex-row"
              >
                {/* ── Left: art panel ── */}
                <div className="flex flex-shrink-0 flex-col items-center gap-3">
                  <ModalArt
                    card={card}
                    rarity={rarity}
                    flipped={flipped}
                    onFlip={() => setFlipped((v) => !v)}
                    onEnlarge={() => setLightboxOpen(true)}
                  />
                  <p className="shop-mono text-center text-[10px] text-[#4a5568]">
                    {idx + 1} / {cards.length}
                  </p>
                </div>

                {/* ── Right: info ── */}
                <div
                  className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto"
                  style={{ maxHeight: "calc(80vh - 80px)" }}
                >
                  {/* Game + rarity + finish + graded badges */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span
                      className="rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                      style={{
                        background: gameCfg.bg,
                        borderColor: `${gameCfg.primary}30`,
                        color: gameCfg.primary,
                      }}
                    >
                      {game}
                    </span>
                    <span
                      className="rounded-sm border px-2 py-0.5 text-[10px] font-semibold"
                      style={{
                        background: `${rarityCfg.color}14`,
                        borderColor: `${rarityCfg.color}38`,
                        color: rarityCfg.color,
                      }}
                    >
                      {rarity}
                    </span>
                    {card.finish && (
                      <span
                        className="rounded-sm border px-2 py-0.5 text-[10px] font-semibold"
                        style={{
                          background: "rgba(168,85,247,0.12)",
                          borderColor: "rgba(168,85,247,0.3)",
                          color: "#d8b4fe",
                        }}
                      >
                        ✦ {card.finish}
                      </span>
                    )}
                    {card.graded && card.gradingCompany && (
                      <span
                        className="rounded-sm border px-2 py-0.5 text-[10px] font-bold"
                        style={{
                          background: "rgba(201,168,76,0.15)",
                          borderColor: "rgba(201,168,76,0.4)",
                          color: "#f0c96e",
                        }}
                      >
                        ✓ {card.gradingCompany} Graded
                      </span>
                    )}
                  </div>

                  {/* Name */}
                  <div>
                    <h2 className="shop-display text-[clamp(16px,3vw,22px)] font-extrabold leading-tight tracking-wide text-foreground">
                      {card.name}
                    </h2>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                      {card.setName}
                      <span className="mx-1.5 text-[#1e2d4a]">·</span>
                      <span className="shop-mono text-[11px] text-[#4d9ef7]">
                        {card.cardNumber}
                      </span>
                    </p>
                  </div>

                  {/* Type / subtype chips — defensive filter strips
                      legacy junk like "Magic: The Gathering" that
                      leaked into tcgTypes pre-TNK-128. */}
                  {(() => {
                    const cleanTypes = (card.tcgTypes ?? []).filter(
                      (t) => t && t !== "Magic: The Gathering",
                    );
                    const cleanSubtypes = card.tcgSubtypes ?? [];
                    if (cleanTypes.length === 0 && cleanSubtypes.length === 0)
                      return null;
                    return (
                      <div className="flex flex-wrap items-center gap-2">
                        {cleanTypes.map((t) => {
                          const cfg = pokeTypeColor[t] ?? {
                            bg: "rgba(122,138,170,0.15)",
                            text: "#7a8aaa",
                            icon: null,
                          };
                          return (
                            <span
                              key={t}
                              className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold"
                              style={{ background: cfg.bg, color: cfg.text }}
                            >
                              {cfg.icon}
                              {t}
                            </span>
                          );
                        })}
                        {cleanSubtypes.map((s) => (
                          <span
                            key={s}
                            className="rounded border border-white/[0.07] bg-white/[0.04] px-1.5 py-[2px] text-[10px] text-muted-foreground"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Card information — mana cost / HP / P&T / Loyalty.
                      Same stat-row pattern as the full detail page so the
                      buyer's eye lands on the playable specifics last. */}
                  {(card.mtgManaCost ||
                    card.tcgHp ||
                    card.mtgPowerToughness ||
                    card.mtgLoyalty) && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                      {card.mtgManaCost && (
                        <div className="flex items-baseline gap-1.5">
                          <ManaCost cost={card.mtgManaCost} size={15} />
                          <span className="text-[10px] font-semibold text-muted-foreground">
                            Cost
                          </span>
                        </div>
                      )}
                      {card.tcgHp && (
                        <span className="shop-mono flex items-baseline gap-1 text-[18px] font-extrabold text-foreground">
                          {card.tcgHp}
                          <span className="text-[10px] font-semibold text-muted-foreground">
                            HP
                          </span>
                        </span>
                      )}
                      {card.mtgPowerToughness && (
                        <span className="shop-mono flex items-baseline gap-1 text-[18px] font-extrabold text-foreground">
                          {card.mtgPowerToughness}
                          <span className="text-[10px] font-semibold text-muted-foreground">
                            P/T
                          </span>
                        </span>
                      )}
                      {card.mtgLoyalty && !card.mtgPowerToughness && (
                        <span
                          className="shop-mono flex items-baseline gap-1 text-[18px] font-extrabold"
                          style={{ color: "#f0c96e" }}
                        >
                          ◆ {card.mtgLoyalty}
                          <span className="text-[10px] font-semibold text-muted-foreground">
                            Loyalty
                          </span>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Condition (compact in modal) */}
                  <CompactCondition condition={condition} />

                  {/* Price + CTA */}
                  <div
                    className="flex flex-col gap-3 rounded-xl border p-4"
                    style={{
                      background: "linear-gradient(135deg,#0d1627,#0b1120)",
                      borderColor: "rgba(201,168,76,0.15)",
                      boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 30px ${rarityCfg.glow}`,
                    }}
                  >
                    <div className="flex flex-wrap items-end justify-between gap-2">
                      <div>
                        <p className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
                          Shop Price
                        </p>
                        <p className="shop-mono text-[clamp(24px,4vw,30px)] font-bold leading-none tracking-tight text-primary">
                          ${price.toFixed(2)}
                          <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">
                            USD
                          </span>
                        </p>
                      </div>

                      {isAuction ? (
                        <motion.a
                          key="bid"
                          href={card.ebayListingUrl ?? "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="shop-display inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-4 py-2.5 text-[12px] font-bold tracking-wide"
                          style={{
                            background:
                              "linear-gradient(135deg,rgba(168,85,247,0.25),rgba(168,85,247,0.14))",
                            borderColor: "rgba(168,85,247,0.5)",
                            color: "#c084fc",
                            boxShadow: "0 4px 16px rgba(168,85,247,0.2)",
                          }}
                        >
                          <Gavel size={13} />
                          Bid on eBay
                          <ExternalLink size={11} />
                        </motion.a>
                      ) : (
                        <AnimatePresence mode="wait">
                          {!isReserved ? (
                            <motion.button
                              key="add"
                              type="button"
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              onClick={() => onReserve(card.id)}
                              className="shop-display inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-4 py-2.5 text-[12px] font-bold tracking-wide"
                              style={{
                                background:
                                  "linear-gradient(135deg,rgba(201,168,76,0.25),rgba(240,201,110,0.14))",
                                borderColor: "rgba(201,168,76,0.5)",
                                color: "#f0c96e",
                                boxShadow: "0 4px 16px rgba(201,168,76,0.2)",
                              }}
                            >
                              <ShoppingBag size={13} />
                              Reserve
                            </motion.button>
                          ) : (
                            <motion.div
                              key="reserved"
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[12px] font-semibold"
                              style={{
                                background: "rgba(74,222,128,0.1)",
                                borderColor: "rgba(74,222,128,0.3)",
                                color: "#4ade80",
                              }}
                            >
                              <Check size={13} />
                              Reserved
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>

                    <p className="border-t border-white/[0.05] pt-2.5 text-[10px] leading-relaxed text-[#4a5568]">
                      {isAuction
                        ? "Card is currently up for auction on eBay — bid there to win it."
                        : "Reservations are requests — the seller confirms availability and follows up by email."}
                    </p>

                    <AnimatePresence>
                      {isReserved && !isAuction && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          {!removeConfirm ? (
                            <button
                              type="button"
                              onClick={() => setRemoveConfirm(true)}
                              className="text-[11px] text-muted-foreground transition-colors hover:text-destructive"
                            >
                              Remove from reservation
                            </button>
                          ) : (
                            <div className="flex items-center gap-3">
                              <span className="text-[11px] text-muted-foreground">
                                Remove this card?
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  onRemove(card.id);
                                  setRemoveConfirm(false);
                                }}
                                className="text-[11px] font-semibold text-destructive"
                              >
                                Yes, remove
                              </button>
                              <button
                                type="button"
                                onClick={() => setRemoveConfirm(false)}
                                className="text-[11px] text-muted-foreground"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Rules text intentionally hidden — see TNK-121 follow-up.
                      Modal real estate is tight; the buyer reads rules off
                      the card photo. mtgOracleText still rides on the
                      payload so a future tab can re-enable it. */}

                  {/* Details table */}
                  {metaRows.length > 0 && (
                    <div className="overflow-hidden rounded-lg border border-white/[0.07]">
                      <div className="border-b border-white/[0.06] bg-white/[0.03] px-3 py-2">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                          Card Details
                        </p>
                      </div>
                      <div className="bg-[#0d1220] px-3">
                        {metaRows.map(({ label, value }) => (
                          <div
                            key={label}
                            className="flex items-start justify-between gap-3 border-b border-white/[0.045] py-2 last:border-b-0"
                          >
                            <span className="flex-shrink-0 text-[11px] text-muted-foreground">
                              {label}
                            </span>
                            <span className="text-right text-[11px] text-foreground/80">
                              {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Full page link — for URL-share use case */}
                  <Link
                    to="/shop/card/$id"
                    params={{ id: card.id }}
                    className="inline-flex items-center gap-1.5 self-start text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ExternalLink size={11} />
                    Open full page
                  </Link>

                  <p className="shop-mono text-[9px] text-[#1e2d4a]">
                    {card.id}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom nav bar — TNK-115 phase B2 polish. Was floating prev/next
              arrows pinned to the modal's vertical center which overlapped
              the price card. Now lives in its own footer row so content can
              breathe. Visible only when there's somewhere to go. */}
          {(hasPrev || hasNext) && (
            <div className="flex items-center justify-between gap-3 border-t border-white/[0.05] bg-white/[0.02] px-4 py-2.5">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={!hasPrev}
                aria-label="Previous card"
                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 text-[11px] text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft size={13} />
                Prev
              </button>
              <span className="shop-mono text-[9px] text-muted-foreground opacity-50">
                ← / → · esc to close
              </span>
              <button
                type="button"
                onClick={() => navigate(1)}
                disabled={!hasNext}
                aria-label="Next card"
                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 text-[11px] text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
              >
                Next
                <ChevronRight size={13} />
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Lightbox sits as a sibling of the modal so its z-60 backdrop covers
          the modal's z-50 backdrop. Esc closes the lightbox first, then a
          second Esc closes the modal — the lightbox's keyboard handler
          mounts only while lightboxOpen is true. */}
      <CardLightbox
        card={lightboxOpen ? card : null}
        onClose={() => setLightboxOpen(false)}
      />
    </AnimatePresence>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

/**
 * The card art panel with the 3D rotateY card flip. Canonical two-image
 * pattern: both images stay mounted, each carries `backface-visibility:
 * hidden`, and the back image is pre-rotated 180° so it shows up right-
 * side-up when the wrapper completes its 180° turn. Past the 90° midpoint
 * the front image's back-face becomes hidden and the back image's
 * front-face becomes visible — the visual effect is a single uninterrupted
 * flip instead of the snap-then-rotate "double flip" that the earlier
 * image-swap pattern produced.
 *
 * Glow / shimmer overlays sit OUTSIDE the flipping element so they don't
 * mirror with it.
 */
function ModalArt({
  card,
  rarity,
  flipped,
  onFlip,
  onEnlarge,
}: {
  card: StorefrontCard;
  rarity: Rarity;
  flipped: boolean;
  onFlip: () => void;
  onEnlarge: () => void;
}) {
  const rarityCfg = rarityConfig[rarity];
  // TNK-125: medium variant — modal art panel renders at ~480×672.
  const frontUrl = card.imageFrontRef
    ? imageUrl(card.imageFrontRef, "medium")
    : null;
  const backUrl = card.imageBackRef
    ? imageUrl(card.imageBackRef, "medium")
    : null;
  const [frontErr, setFrontErr] = useState(false);
  const [backErr, setBackErr] = useState(false);

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={onEnlarge}
        aria-label="Enlarge photo"
        className="group/enlarge relative rounded-xl"
        style={{
          width: "200px",
          aspectRatio: "2.5/3.5",
          perspective: "1200px",
        }}
      >
        <motion.div
          className="relative h-full w-full"
          style={{ transformStyle: "preserve-3d" }}
          initial={false}
          animate={{ rotateY: flipped ? 180 : 0 }}
          // tween (not spring) to remove the bouncy double-tap feel; longer
          // duration so the flip reads as a real card turning over rather
          // than a snap.
          transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Front face */}
          <div
            className="absolute inset-0 overflow-hidden rounded-xl border"
            style={{
              background: "linear-gradient(160deg,#0d1627,#0a0f1e)",
              borderColor: `${rarityCfg.color}33`,
              boxShadow: `0 24px 60px rgba(0,0,0,0.75), 0 0 40px ${rarityCfg.glow}`,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
          >
            {frontUrl && !frontErr ? (
              <img
                src={frontUrl}
                alt={card.name}
                className="h-full w-full object-contain"
                onError={() => setFrontErr(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span
                  className="shop-display text-[8px] uppercase tracking-[0.2em]"
                  style={{ color: rarityCfg.color, opacity: 0.5 }}
                >
                  {card.game}
                </span>
              </div>
            )}
          </div>

          {/* Back face — pre-rotated 180° so it lands upright at the end of
              the wrapper's 180° rotation. */}
          <div
            className="absolute inset-0 overflow-hidden rounded-xl border"
            style={{
              background: "linear-gradient(160deg,#0d1627,#0a0f1e)",
              borderColor: `${rarityCfg.color}33`,
              boxShadow: `0 24px 60px rgba(0,0,0,0.75), 0 0 40px ${rarityCfg.glow}`,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            {backUrl && !backErr ? (
              <img
                src={backUrl}
                alt={`${card.name} (back)`}
                className="h-full w-full object-contain"
                onError={() => setBackErr(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span
                  className="shop-display text-[8px] uppercase tracking-[0.2em]"
                  style={{ color: rarityCfg.color, opacity: 0.5 }}
                >
                  back
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Holo shimmer overlay sits outside the flipping element so it
            doesn't mirror with the back face. */}
        {(rarity === "Mythic" || rarity === "Promo" || card.finish) && (
          <div
            className="shop-holo-rarity pointer-events-none absolute inset-0 rounded-xl"
            style={{
              background: `linear-gradient(135deg, transparent 0%, ${rarityCfg.color}29 25%, rgba(77,158,247,0.1) 50%, ${rarityCfg.color}29 75%, transparent 100%)`,
            }}
          />
        )}

        {/* Trust signal — actual card photo, not stock. Outside the
            flipping element so it stays static through the flip. */}
        <div className="pointer-events-none absolute bottom-2 left-2 z-10 inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/55 px-2 py-0.5 text-[9px] font-medium text-white/85 backdrop-blur-sm">
          <Camera size={9} />
          Actual card photo
        </div>

        {/* Enlarge affordance — top-right corner. Hidden by default on
            hover-capable devices and revealed on hover so it doesn't
            compete with the trust badge; always visible on touch devices
            (no hover available) so mobile buyers have a clear cue that
            the image is tappable. */}
        <div className="pointer-events-none absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/45 px-2 py-0.5 text-[9px] text-white/70 opacity-0 backdrop-blur-sm transition-opacity group-hover/enlarge:opacity-100 [@media(hover:none)]:opacity-100">
          <ZoomIn size={9} />
          Tap to enlarge
        </div>
      </button>

      {backUrl && (
        <button
          type="button"
          onClick={onFlip}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground"
        >
          <RefreshCw size={10} />
          {flipped ? "Front" : "Back"}
        </button>
      )}
    </div>
  );
}

function CompactCondition({ condition }: { condition: ConditionCode }) {
  const cfg = conditionConfig[condition];
  return (
    <div
      className="flex items-center gap-2.5 rounded-lg border p-2.5"
      style={{
        background: `${cfg.color}0d`,
        borderColor: `${cfg.color}28`,
      }}
    >
      <span
        className="shop-mono flex-shrink-0 rounded-sm px-2 py-0.5 text-[11px] font-bold"
        style={{ background: `${cfg.color}20`, color: cfg.color }}
      >
        {condition}
      </span>
      <div>
        <p className="text-[11px] font-semibold" style={{ color: cfg.color }}>
          {cfg.label}
        </p>
        <p className="text-[10px] text-muted-foreground">{cfg.desc}</p>
      </div>
    </div>
  );
}
